/*
 * The quote page for an auto quote, laid out like the website's instant quote (thecleanersusa.com/quote/, which the old
 * Steward's quote page ran itself, quoteweb.php): Your price, then Book, with the quote on a card beside it. The client
 * makes the home smaller to pay less or adds a clean, a schedule or add-ons (a lawn: the schedule and add-ons), every
 * change priced again by the server.
 *
 * Book (Settings, Booking from a quote, on): a week at a time (This week, Next week, In 2 weeks, In 3 weeks), each day
 * with its Morning and Afternoon room, then name, signature and card, and one button that saves the card and books.
 * With booking off (the GroundsMen), the second step is Approve instead: name, signature and card, and one Approve and
 * save card button, like the old GroundsMen quote page; the office books the day. On a phone the price and the main
 * button stay at the bottom of the screen (owner, 2026-10-30), so a client who doesn't want to change anything taps once.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  repeatWords,
  smallerHome,
  type HomeAnswers,
  type HourlyAnswers,
  type LawnAnswers,
  type PublicAutoQuote,
  type PublicQuote,
} from "@steward/shared";
import { api, quoteFileSrc } from "../api/client";
import { CardBox, CardTermsLink, useCardFields } from "../components/CardFields";
import { moneyExact, phoneText } from "../components/format";
import { Banner, Button } from "../components/parts";
import { SignaturePad } from "../components/SignaturePad";
import { errText } from "../components/work";
import { SectionsShown } from "./QuoteSections";

type Home = Extract<PublicAutoQuote, { kind: "home" }>;
type Lawn = Extract<PublicAutoQuote, { kind: "lawn" }>;
type Hourly = Extract<PublicAutoQuote, { kind: "hourly" }>;
type Day = NonNullable<PublicQuote["booking"]>["days"][number];
type TimeId = "morning" | "afternoon" | "flexible";

const TIMES: { id: TimeId; label: string; sub: string }[] = [
  { id: "morning", label: "Morning", sub: "Arrive between 8 AM and 12 PM" },
  { id: "afternoon", label: "Afternoon", sub: "Arrive between 12 PM and 5 PM" },
  { id: "flexible", label: "Flexible", sub: "Any time that day. Easiest to fit in." },
];
const WEEKS = ["This week", "Next week", "In 2 weeks", "In 3 weeks"];

const day = (iso: string) => new Date(`${iso}T12:00:00`);
const weekday = (iso: string) => day(iso).toLocaleDateString("en-US", { weekday: "short" });
const shortDay = (iso: string) => day(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const longDay = (iso: string) => day(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const slotOpen = (d: Day, t: TimeId) => d.open && (t === "flexible" || d[t]);
/** A day's room in plain words, in place of AM and PM tags. */
const roomWords = (d: Day) =>
  !d.open
    ? "No availability"
    : d.morning && !d.afternoon
      ? "Morning only"
      : d.afternoon && !d.morning
        ? "Afternoon only"
        : "Open";
/** The day and time picked, said the way we'd say it on the phone. */
const pickedWords = (iso: string, t: TimeId) =>
  `${longDay(iso)}, ${t === "morning" ? "in the morning, 8 AM to 12 PM" : t === "afternoon" ? "in the afternoon, 12 PM to 5 PM" : "any time that day"}`;
/** "Every 2 weeks on Tuesdays" to run on in a sentence: "every 2 weeks on Tuesdays". */
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

/** What the quote costs now: the first visit, and each visit after it on a schedule. */
function totalsOf(q: PublicQuote) {
  const auto = q.auto;
  const total = q.lines.reduce((s, l) => s + (l.optional && !l.picked ? 0 : l.amountCents), 0);
  const repeat = q.lines.filter((l) => l.recurring).reduce((s, l) => s + l.amountCents, 0);
  const often = auto ? auto.often.find((o) => o.id === auto.answers.often)?.label : undefined;
  return { total, repeat, then: repeat > 0 && often ? `Then ${moneyExact(repeat)} ${often.toLowerCase()}` : null };
}

export function AutoQuoteBody({ token, q }: { token: string; q: PublicQuote }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<"price" | "go">("price");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const auto = q.auto;
  const booking = q.booking;
  const change = async (ask: Partial<HomeAnswers> | Partial<LawnAnswers> | Partial<HourlyAnswers>) => {
    setBusy(true);
    setErr(null);
    try {
      const after = await api<PublicQuote>("POST", `public/quote/${token}/change`, { answers: ask });
      qc.setQueryData(["public-quote", token], after);
    } catch (e) {
      setErr(errText(e, "We could not change that. Text us and we'll help."));
    } finally {
      setBusy(false);
    }
  };
  if (q.bookedFor) return <Booked q={q} />;
  if (!auto) return null;
  const t = totalsOf(q);
  const next = () => {
    setStep("go");
    toTop();
  };
  const goWord = booking ? "Book this price" : "Approve this price";
  return (
    <div className="aq">
      <div className="aq-main">
        <ol className="aq-steps" aria-label="Steps">
          <li className={step === "price" ? "on" : "done"}>
            <span>1</span> Your price
          </li>
          <li className={step === "go" ? "on" : ""}>
            <span>2</span> {booking ? "Pick a day" : "Approve"}
          </li>
        </ol>
        {step === "price" ? (
          <>
            <header className="aq-head">
              <h1>Hi {q.firstName}, here's your price</h1>
              <p className="muted">Change anything below and the price updates. There's nothing to pay today.</p>
            </header>
            <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="intro" />
            {q.message ? <p className="pre-wrap">{q.message}</p> : null}
            {auto.kind === "home" ? (
              <HomeChoices a={auto} busy={busy} change={(x) => void change(x)} />
            ) : auto.kind === "hourly" ? (
              <HourlyChoices a={auto} busy={busy} change={(x) => void change(x)} />
            ) : (
              <LawnChoices a={auto} busy={busy} change={(x) => void change(x)} />
            )}
            <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="rest" />
            {err ? <Banner type="error">{err}</Banner> : null}
            <div className="aq-go">
              <Button size="large" fullWidth disabled={busy} onClick={next}>
                {goWord}
              </Button>
            </div>
          </>
        ) : (
          <GoStep token={token} q={q} auto={auto} booking={booking} back={() => setStep("price")} />
        )}
      </div>
      <aside className="aq-card">
        <p className="aq-kicker">Your quote</p>
        <b className="aq-total">{moneyExact(t.total)}</b>
        {t.then && t.repeat !== t.total ? <p className="aq-then">{t.then}</p> : null}
        <ul className="aq-lines">
          {q.lines
            .filter((l) => !l.optional || l.picked)
            .map((l) => (
              <li key={l.id}>
                <span>{l.description}</span>
                <span className="num">{moneyExact(l.amountCents)}</span>
              </li>
            ))}
        </ul>
        {step === "price" ? (
          <button type="button" className="aq-book" disabled={busy} onClick={next}>
            {goWord}
          </button>
        ) : null}
        <p className="aq-note">Nothing to pay today. You pay after the visit.</p>
      </aside>
      {step === "price" ? (
        <StickyBar total={t.total} then={t.then} busy={busy} label={booking ? "Book" : "Approve"} go={next} />
      ) : null}
    </div>
  );
}

/** The phone's bottom bar: the price, and the main button. */
function StickyBar({
  total,
  then,
  busy,
  label,
  go,
  err,
}: {
  total: number;
  then: string | null;
  busy: boolean;
  label: string;
  go: () => void;
  err?: string | null;
}) {
  return (
    <div className="aq-bar" role="region" aria-label="Your price">
      {err ? <p className="aq-bar-err">{err}</p> : null}
      <div className="aq-bar-row">
        <div className="aq-bar-price">
          <b>{moneyExact(total)}</b>
          <span>{then ?? "Nothing to pay today"}</span>
        </div>
        <button type="button" className="aq-bar-go" disabled={busy} onClick={go}>
          {busy ? "One moment" : label}
        </button>
      </div>
    </div>
  );
}

function Stepper({
  label,
  hint,
  value,
  min,
  step = 1,
  busy,
  set,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  step?: number;
  busy: boolean;
  set: (n: number) => void;
}) {
  return (
    <div className="aq-stepper">
      <span className="aq-label">{label}</span>
      <div className="aq-stepper-row">
        <button
          type="button"
          aria-label={`Fewer ${label.toLowerCase()}`}
          disabled={busy || value <= min}
          onClick={() => set(Math.round((value - step) * 2) / 2)}
        >
          −
        </button>
        <b aria-live="polite">{value}</b>
        <button
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={busy || value >= 20}
          onClick={() => set(Math.round((value + step) * 2) / 2)}
        >
          +
        </button>
      </div>
      {hint ? <span className="muted small">{hint}</span> : null}
    </div>
  );
}

function HomeChoices({ a, busy, change }: { a: Home; busy: boolean; change: (x: Partial<HomeAnswers>) => void }) {
  const x = a.answers;
  const smaller = smallerHome(a.sent, x);
  return (
    <div className="aq-parts">
      <section>
        <h2>How big is it?</h2>
        {x.sizeBy === "sqft" ? (
          <p>{x.sqft.toLocaleString("en-US")} sq ft</p>
        ) : (
          <>
            <p className="muted small">Kitchen and living room are already included. Don't count hallways, closets or laundry.</p>
            <div className="aq-steppers">
              <Stepper label="Bedrooms" value={x.beds} min={0} busy={busy} set={(n) => change({ beds: n })} />
              <Stepper
                label="Bathrooms"
                hint="A half bath is 0.5."
                value={x.baths}
                min={1}
                step={0.5}
                busy={busy}
                set={(n) => change({ baths: n })}
              />
              <Stepper
                label="Other rooms"
                hint="Dining room, office, den."
                value={x.other}
                min={0}
                busy={busy}
                set={(n) => change({ other: n })}
              />
            </div>
          </>
        )}
        {smaller ? (
          <p className="aq-warn">Your home is smaller than we quoted. When you book, tell us which rooms not to clean.</p>
        ) : null}
      </section>
      <section>
        <h2>What kind of clean?</h2>
        <div className="aq-options" role="radiogroup">
          {a.services
            .filter((s) => s.cents !== null && (x.sizeBy !== "sqft" || s.sqftOk))
            .map((s) => (
              <label key={s.id} className={`aq-option${s.id === x.serviceId ? " on" : ""}`}>
                <input
                  type="radio"
                  name="service"
                  disabled={busy}
                  checked={s.id === x.serviceId}
                  onChange={() => change({ serviceId: s.id, ...(s.repeats ? {} : { often: "once" }) })}
                />
                <span>{s.label}</span>
                <span className="num">{moneyExact(s.cents ?? 0)}</span>
              </label>
            ))}
        </div>
      </section>
      {a.nudge && x.often === "once" ? (
        <div className="aq-nudge">
          <p>
            On a schedule, each visit is <b>{moneyExact(a.nudge.perVisitCents)}</b> {a.nudge.label.toLowerCase()}.
          </p>
          <Button
            type="secondary"
            disabled={busy}
            onClick={() => change({ serviceId: a.nudge!.serviceId, often: a.nudge!.id as HomeAnswers["often"] })}
          >
            Switch to {a.nudge.label.toLowerCase()}
          </Button>
        </div>
      ) : null}
      {a.often.length > 1 ? (
        <section>
          <h2>How often?</h2>
          {a.planStart ? (
            <p className="muted small">Your home was cleaned lately, so your first visit is a regular visit.</p>
          ) : null}
          <div className="aq-options" role="radiogroup">
            {a.often.map((o) => (
              <label key={o.id} className={`aq-option${o.id === x.often ? " on" : ""}`}>
                <input
                  type="radio"
                  name="often"
                  disabled={busy}
                  checked={o.id === x.often}
                  onChange={() => change({ often: o.id as HomeAnswers["often"] })}
                />
                <span>{o.label}</span>
                <span className="num">
                  {o.cents === null ? "" : `${moneyExact(o.cents)} ${a.planStart ? "a visit" : "a visit after the first"}`}
                </span>
              </label>
            ))}
          </div>
        </section>
      ) : null}
      {a.extras.length ? (
        <section>
          <h2>Add-ons for the first visit</h2>
          <div className="aq-options">
            {a.extras.map((e) => {
              const v = x.extras[e.id];
              return e.each ? (
                <div key={e.id} className="aq-option">
                  <span>
                    {e.label}{" "}
                    <span className="muted small">
                      {moneyExact(e.cents)} each{e.listCents !== null ? `, plan price (usually ${moneyExact(e.listCents)})` : ""}
                    </span>
                  </span>
                  <Stepper
                    label={e.label}
                    value={typeof v === "number" ? v : 0}
                    min={0}
                    busy={busy}
                    set={(n) => change({ extras: { ...x.extras, [e.id]: n } })}
                  />
                </div>
              ) : (
                <label key={e.id} className={`aq-option${v ? " on" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={Boolean(v)}
                    onChange={(ev) => change({ extras: { ...x.extras, [e.id]: ev.target.checked } })}
                  />
                  <span>
                    {e.label}
                    {e.listCents !== null ? (
                      <span className="muted small aq-plan">Plan price, usually {moneyExact(e.listCents)}</span>
                    ) : null}
                  </span>
                  <span className="num">+{moneyExact(e.cents)}</span>
                </label>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** Maid service by the hour: how many cleaners, how long, how often. Every visit costs the same. */
function HourlyChoices({ a, busy, change }: { a: Hourly; busy: boolean; change: (x: Partial<HourlyAnswers>) => void }) {
  const x = a.answers;
  return (
    <div className="aq-parts">
      <section>
        <h2>How many cleaners, for how long?</h2>
        <p className="muted small">{moneyExact(a.rateCents)} an hour for each cleaner.</p>
        <div className="aq-steppers">
          <Stepper
            label="Cleaners"
            value={x.cleaners}
            min={1}
            busy={busy}
            set={(n) => change({ cleaners: Math.min(6, Math.round(n)) })}
          />
          <Stepper label="Hours" value={x.hours} min={1} step={0.5} busy={busy} set={(n) => change({ hours: Math.min(10, n) })} />
        </div>
      </section>
      <section>
        <h2>How often?</h2>
        <div className="aq-options" role="radiogroup">
          {a.often.map((o) => (
            <label key={o.id} className={`aq-option${o.id === x.often ? " on" : ""}`}>
              <input
                type="radio"
                name="often"
                disabled={busy}
                checked={o.id === x.often}
                onChange={() => change({ often: o.id as HourlyAnswers["often"] })}
              />
              <span>{o.label}</span>
              <span className="num">{o.cents === null ? "" : `${moneyExact(o.cents)} a visit`}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function LawnChoices({ a, busy, change }: { a: Lawn; busy: boolean; change: (x: Partial<LawnAnswers>) => void }) {
  const x = a.answers;
  return (
    <div className="aq-parts">
      <section>
        <h2>Your yard</h2>
        <p>{x.sqft.toLocaleString("en-US")} sq ft of grass</p>
      </section>
      <section>
        <h2>How often?</h2>
        <div className="aq-options" role="radiogroup">
          {a.often
            .filter((o) => o.cents !== null)
            .map((o) => (
              <label key={o.id} className={`aq-option${o.id === x.often ? " on" : ""}`}>
                <input
                  type="radio"
                  name="often"
                  disabled={busy}
                  checked={o.id === x.often}
                  onChange={() => change({ often: o.id as LawnAnswers["often"] })}
                />
                <span>{o.label}</span>
                <span className="num">
                  {o.perVisitCents !== null ? `${moneyExact(o.perVisitCents)} a visit` : moneyExact(o.cents ?? 0)}
                </span>
              </label>
            ))}
        </div>
      </section>
      <section>
        <h2>Add-ons</h2>
        <div className="aq-options">
          <label className={`aq-option${x.bagging ? " on" : ""}`}>
            <input type="checkbox" disabled={busy} checked={x.bagging} onChange={(e) => change({ bagging: e.target.checked })} />
            <span>Bag clippings, bags left for your trash pickup</span>
            <span className="num">+{moneyExact(a.baggingCents)} a visit</span>
          </label>
          <label className={`aq-option${x.weeds ? " on" : ""}`}>
            <input type="checkbox" disabled={busy} checked={x.weeds} onChange={(e) => change({ weeds: e.target.checked })} />
            <span>Lawn weed control treatment</span>
            <span className="num">+{moneyExact(a.weedsCents)}</span>
          </label>
        </div>
      </section>
      <section>
        <h2>Hedge and bush trimming</h2>
        <div className="aq-options" role="radiogroup">
          {[{ id: "none", label: "None", cents: 0 as number | null }, ...a.hedges].map((h) => {
            const on = (x.hedges ?? "none") === h.id;
            return (
              <label key={h.id} className={`aq-option${on ? " on" : ""}`}>
                <input
                  type="radio"
                  name="hedges"
                  disabled={busy}
                  checked={on}
                  onChange={() => change({ hedges: h.id === "none" ? null : (h.id as LawnAnswers["hedges"]) })}
                />
                <span>{h.label}</span>
                <span className="num">
                  {h.id === "none" ? "" : h.cents === null ? "Priced from photos" : `+${moneyExact(h.cents)}`}
                </span>
              </label>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/** Step 2: pick a day and a time (booking on), then name, signature and card, and one button. */
function GoStep({
  token,
  q,
  auto,
  booking,
  back,
}: {
  token: string;
  q: PublicQuote;
  auto: PublicAutoQuote;
  booking: PublicQuote["booking"];
  back: () => void;
}) {
  const qc = useQueryClient();
  // Their cleaner from last time (a plan offer): that cleaner's open days, or any cleaner for more days (owner,
  // 2026-11-02: "they'll want their last cleaner").
  const cleaner = booking?.cleaner ?? null;
  const [anyCleaner, setAnyCleaner] = useState(false);
  const days = useMemo(
    () => (booking?.days ?? []).map((d) => (cleaner && !anyCleaner ? { ...d, open: d.withCleaner } : d)),
    [booking, cleaner, anyCleaner],
  );
  const weeks = useMemo(
    () => WEEKS.map((label, i) => ({ label, days: days.slice(i * 7, i * 7 + 7) })).filter((w) => w.days.length > 0),
    [days],
  );
  // One week of days at a time, all on screen; the week buttons are the only way to move (owner, 2026-10-30: easy for
  // an older client). Opens on the first week with an open day.
  const [week, setWeek] = useState(() =>
    Math.max(
      0,
      weeks.findIndex((w) => w.days.some((d) => d.open)),
    ),
  );
  const phone = q.brand.phone;
  const [picked, setPicked] = useState<string | null>(null);
  // Pick a day for me (owner, 2026-11-02): the dates they can do and their arrival time; we book the soonest open day.
  const [forMe, setForMe] = useState(false);
  const [from, setFrom] = useState(() => days[0]?.date ?? "");
  const [to, setTo] = useState(() => days[13]?.date ?? days[days.length - 1]?.date ?? "");
  const [time, setTime] = useState<TimeId>("flexible");
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const smaller = auto.kind === "home" && smallerHome(auto.sent, auto.answers);
  const needCard = !q.hasCard && (booking ? booking.cardFirst : q.cardRequired);
  const card = useCardFields(token, needCard);
  const pickedDay = forMe ? null : (days.find((d) => d.date === picked) ?? null);
  // A repeat is named from its first day: "Every 2 weeks on Tuesdays", "Monthly on the 1st Monday" (same weekday of the
  // month, so routes stay steady; owner, 2026-11-02).
  const often = auto.kind === "home" || auto.kind === "hourly" ? auto.answers.often : null;
  const repeatOn = (d: string) =>
    often && often !== "once" ? repeatWords({ frequency: often, startDate: d, repeatDay: null, repeatRule: null }) : null;
  const t = totalsOf(q);
  // A day without room in the time picked goes back to Flexible.
  useEffect(() => {
    if (pickedDay && !slotOpen(pickedDay, time)) setTime("flexible");
  }, [pickedDay, time]);

  const missing = (): string | null => {
    if (booking && forMe && (!from || !to || from > to)) return "Pick the first and last day that work for you.";
    if (booking && !forMe && !picked) return "Pick a day.";
    if (smaller && !note.trim()) return "Tell us which rooms not to clean.";
    if (name.trim().length < 2) return "Type your full name.";
    if (q.signRequired && !drawing) return "Sign in the box with your finger.";
    return null;
  };
  const go = async () => {
    const why = missing();
    if (why) {
      setErr(why);
      if (booking && !picked) document.getElementById("aq-days")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (needCard) qc.setQueryData(["public-quote", token], await card.save());
      const after = await api<PublicQuote>("POST", `public/quote/${token}/approve`, {
        picked: [],
        signature: name.trim(),
        drawing,
        ...(booking && forMe
          ? { book: { pickFrom: from, pickTo: to, timeOfDay: time, anyCleaner, note: note.trim() } }
          : booking && picked
            ? { book: { day: picked, timeOfDay: time, anyCleaner, note: note.trim() } }
            : {}),
      });
      qc.setQueryData(["public-quote", token], after);
      toTop();
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
    } finally {
      setBusy(false);
    }
  };
  const label = booking
    ? forMe
      ? "Book my soonest day"
      : picked
        ? `Book ${shortDay(picked)}`
        : "Pick a day"
    : needCard
      ? "Approve and save card"
      : "Approve quote";

  return (
    <div className="aq-parts">
      <button type="button" className="link aq-back" onClick={back}>
        Back to your price
      </button>
      {booking ? (
        <>
          <header className="aq-head">
            <h1>When should we come?</h1>
            <p className="muted">Pick a day and the best time for us to arrive, or let us pick the soonest day.</p>
          </header>
          {cleaner ? (
            <div className="aq-cleaner">
              <p>
                {anyCleaner ? (
                  <>Days for any of our cleaners.</>
                ) : (
                  <>
                    <b>{cleaner}</b> can keep coming. These are {cleaner}'s open days.
                  </>
                )}
              </p>
              <label className="row">
                <input
                  type="checkbox"
                  checked={anyCleaner}
                  onChange={(e) => {
                    setAnyCleaner(e.target.checked);
                    setPicked(null);
                  }}
                />
                <span>Any cleaner is fine (more days)</span>
              </label>
            </div>
          ) : null}
          <div className="aq-times" role="radiogroup" aria-label="How to pick the day">
            <label className={`aq-time${forMe ? "" : " on"}`}>
              <input type="radio" name="how" checked={!forMe} onChange={() => setForMe(false)} />
              <span>
                <b>I'll pick a day</b>
                <span>See our open days</span>
              </span>
            </label>
            <label className={`aq-time${forMe ? " on" : ""}`}>
              <input type="radio" name="how" checked={forMe} onChange={() => setForMe(true)} />
              <span>
                <b>Pick a day for me</b>
                <span>Tell us the dates that work</span>
              </span>
            </label>
          </div>
          {forMe ? (
            <section id="aq-days">
              <h2>Dates that work for you</h2>
              <div className="aq-range">
                <label className="aq-label">
                  From
                  <div className="input">
                    <input
                      type="date"
                      value={from}
                      min={days[0]?.date}
                      max={days[days.length - 1]?.date}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>
                </label>
                <label className="aq-label">
                  To
                  <div className="input">
                    <input
                      type="date"
                      value={to}
                      min={from || days[0]?.date}
                      max={days[days.length - 1]?.date}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </label>
              </div>
              <p className="muted small">We book the soonest open day in these dates. You see the day as soon as you book.</p>
            </section>
          ) : (
            <section id="aq-days">
              <h2>Pick a day</h2>
              <div className="aq-weeks" role="tablist" aria-label="Week">
                {weeks.map((w, i) => (
                  <button
                    key={w.label}
                    type="button"
                    role="tab"
                    aria-selected={week === i}
                    className={`aq-week${week === i ? " on" : ""}`}
                    onClick={() => setWeek(i)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              <div className="aq-days" role="radiogroup" aria-label="Day">
                {(weeks[week]?.days ?? []).map((d) => (
                  <button
                    key={d.date}
                    type="button"
                    role="radio"
                    aria-checked={picked === d.date}
                    aria-label={`${longDay(d.date)}, ${roomWords(d)}`}
                    className={`aq-day${picked === d.date ? " on" : ""}`}
                    disabled={!d.open}
                    onClick={() => {
                      setPicked(d.date);
                      setErr(null);
                    }}
                  >
                    <span className="aq-day-name">{weekday(d.date)}</span>
                    <b className="aq-day-num">{day(d.date).getDate()}</b>
                    <span className="aq-day-mon">{day(d.date).toLocaleDateString("en-US", { month: "short" })}</span>
                    <span className={`aq-day-room${d.open ? "" : " off"}`}>{roomWords(d)}</span>
                  </button>
                ))}
              </div>
              {!days.some((d) => d.open) ? (
                <Banner type="notice">We're booked up for now. Call us and we'll find a day.</Banner>
              ) : null}
              <p className="muted small">
                Need a day more than 4 weeks out?{" "}
                {phone ? (
                  <>
                    Call or text <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`}>{phoneText(phone)}</a>.
                  </>
                ) : (
                  "Reply to our text and we'll find one."
                )}
              </p>
            </section>
          )}
          <section>
            <h2>Best time to arrive</h2>
            <div className="aq-times" role="radiogroup" aria-label="Best time to arrive">
              {TIMES.map((x) => {
                const open = pickedDay ? slotOpen(pickedDay, x.id) : true;
                return (
                  <label key={x.id} className={`aq-time${time === x.id ? " on" : ""}${open ? "" : " off"}`}>
                    <input type="radio" name="time" disabled={!open} checked={time === x.id} onChange={() => setTime(x.id)} />
                    <span>
                      <b>{x.label}</b>
                      <span>{open ? x.sub : "Full that day"}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {picked && !forMe ? (
              <p className="aq-picked" aria-live="polite">
                You picked <b>{pickedWords(picked, time)}</b>.
                {repeatOn(picked) ? (
                  <>
                    {" "}
                    Then <b>{lowerFirst(repeatOn(picked)!)}</b>.
                  </>
                ) : null}
              </p>
            ) : null}
          </section>
          <section>
            <label className="aq-label" htmlFor="aq-note">
              {smaller ? "Which rooms should we skip?" : "Anything we should know? (optional)"}
            </label>
            <textarea
              id="aq-note"
              className="aq-text"
              rows={3}
              maxLength={600}
              placeholder={smaller ? "Like the guest room and the office" : "Gate code, pets, parking"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </section>
        </>
      ) : (
        <header className="aq-head">
          <h1>Approve your quote</h1>
          <p className="muted">
            {q.expiresOn ? `This price is good until ${longDay(q.expiresOn)}. ` : ""}We'll reach out to set your first day.
          </p>
        </header>
      )}
      <section className="aq-sign">
        <h2>{booking ? "Sign to book" : "Sign to approve"}</h2>
        <label className="aq-label" htmlFor="aq-name">
          Your full name
        </label>
        <div className="input">
          <input id="aq-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        {q.signRequired ? <SignaturePad onChange={setDrawing} label="Sign here" /> : null}
        <p className="muted small">
          By signing you approve {moneyExact(t.total)} for the first visit
          {t.then ? `, then ${t.then.replace(/^Then /, "")}` : ""}.
        </p>
      </section>
      {needCard ? (
        <CardBox
          card={card}
          title={
            booking
              ? "A card on file holds your day. You are not charged now."
              : "A card on file books your service. You are not charged now."
          }
          sentence={
            <>
              {q.saveCardSentence} <CardTermsLink />
            </>
          }
        />
      ) : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      <div className="aq-go">
        <Button size="large" fullWidth loading={busy} onClick={() => void go()}>
          {label}
        </Button>
      </div>
      {/* The bar's button stays short so the repeat price beside it always shows in full. */}
      <StickyBar
        total={t.total}
        then={t.then}
        busy={busy}
        label={booking ? (picked || forMe ? "Book" : "Pick a day") : "Approve"}
        go={() => void go()}
        err={err}
      />
    </div>
  );
}

function Booked({ q }: { q: PublicQuote }) {
  const b = q.bookedFor;
  if (!b) return null;
  return (
    <div className="aq-booked">
      <div className="aq-booked-check" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28">
          <path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
        </svg>
      </div>
      <h1>You're booked, {q.firstName}</h1>
      <p>
        We'll see you <b>{longDay(b.date)}</b>
        {b.window ? `, arriving ${b.window}` : ""}. Thank you for booking with us.
      </p>
      <ul className="aq-booked-lines">
        {q.lines
          .filter((l) => !l.optional || l.picked)
          .map((l) => (
            <li key={l.id}>
              <span>{l.description}</span>
              <span className="num">{moneyExact(l.amountCents)}</span>
            </li>
          ))}
      </ul>
      <p className="muted small">Quote #{q.number}. Nothing to pay today. You pay after the visit.</p>
    </div>
  );
}
